<template>
	<n-modal v-model:show="show" :mask-closable="true" :trap-focus="false">
		<div class="square-share-panel" @click.stop>
			<input
				ref="copyLinkInputRef"
				type="text"
				readonly
				tabindex="-1"
				class="square-share-panel__copy-input"
				aria-hidden="true"
			/>
			<button type="button" class="square-share-panel__close" @click="show = false">
				<img src="../../../images/close.png" alt="" />
			</button>
			<h3 class="square-share-panel__title">{{ t("square.detail.share") }}</h3>
			<div class="square-share-panel__list">
				<button
					v-for="(item, index) in shareList"
					:key="index"
					type="button"
					class="square-share-panel__item"
					@click="onShareItem(item)"
					:aria-label="
						t('square.detail.shareTo', {
							platform: item.text || t('square.detail.sharePlatform'),
						})
					"
				>
					<img :src="item.icon" :alt="item.text || t('square.detail.share')" />
					<span>{{ item.text }}</span>
				</button>
			</div>
			<button type="button" class="square-share-panel__copy-link" @click.stop="handleCopyLink">
				{{ t("square.detail.copyLink") }}
			</button>
		</div>
	</n-modal>
</template>

<script setup>
import { ref } from "vue";
import { NModal } from "naive-ui";
import { useI18n } from "~/composables/useI18n";
import showMessage from "~/utils/showMessage";
import { copySync } from "~/utils/clipboard";

const props = defineProps({
	shareList: {
		type: Array,
		default: () => [],
	},
});

const emit = defineEmits(["share"]);

const show = defineModel("show", { type: Boolean, default: false });

const { t } = useI18n();
const copyLinkInputRef = ref(null);

function onShareItem(item) {
	emit("share", item);
}

function handleCopyLink() {
	if (!import.meta.client) return;
	const url = window.location.href;

	emit("share", {
		text: "copy_link",
		shareFn: () => {
			const input = copyLinkInputRef.value;
			if (input) {
				input.value = url;
				input.focus({ preventScroll: true });
				input.select();
				input.setSelectionRange(0, url.length);
				if (document.execCommand("copy")) {
					showMessage(t("square.detail.copyLinkSuccess"), "success");
					return;
				}
			}

			if (copySync(url)) {
				showMessage(t("square.detail.copyLinkSuccess"), "success");
				return;
			}

			navigator.clipboard
				?.writeText(url)
				.then(() => showMessage(t("square.detail.copyLinkSuccess")))
				.catch(() => showMessage(t("square.detail.copyLinkFailed"), "error"));
		},
	});
}
</script>

<style lang="scss" scoped>
.square-share-panel {
	position: relative;
	width: 420px;
	padding: 32px 28px 28px;
	background: #ffffff;
	border-radius: 12px;
	box-sizing: border-box;

	&__copy-input {
		position: absolute;
		width: 1px;
		height: 1px;
		padding: 0;
		border: 0;
		opacity: 0;
		pointer-events: none;
	}

	&__close {
		position: absolute;
		top: 12px;
		right: 12px;
		width: 32px;
		height: 32px;
		border: none;
		background: transparent;
		font-size: 24px;
		line-height: 1;
		color: #999999;
		cursor: pointer;
		padding: 0;

		&:hover {
			color: #666666;
		}

		img {
			width: 20px;
			height: 20px;
		}
	}

	&__title {
		margin: 0 0 24px;
		font-size: 18px;
		font-weight: 700;
		color: #333333;
		text-align: left;
	}

	&__list {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 20px 16px;
	}

	&__item {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		padding: 0;
		border: none;
		background: transparent;
		cursor: pointer;
		transition: transform 0.15s ease;

		&:hover {
			transform: scale(1.05);
		}

		img {
			width: 48px;
			height: 48px;
			object-fit: cover;
			border-radius: 50%;
			display: block;
		}

		span {
			font-size: 14px;
			color: #333333;
			white-space: nowrap;
		}
	}

	&__copy-link {
		display: block;
		width: 100%;
		font-size: 18px;
		color: #333333;
		border: 1px solid #333333;
		border-radius: 8px;
		text-align: center;
		padding: 10px 0;
		font-weight: 500;
		margin-top: 40px;
		background: transparent;
		cursor: pointer;
	}
}
</style>
